import { logError } from '../../shared/utils/logger';
import { BASE_URL, fetchWithAuth } from './client';

/**
 * User details and membership
 * RQ: { userId: string }
 * RS: { status: 200, data: { email: string, membership: string, credit: number } }
 */
export async function fetchUserDetails(payload = { userId: "" }) {
    const userId = encodeURIComponent(payload.userId || "");
    const response = await fetchWithAuth(`${BASE_URL}/api/user/details?userId=${userId}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("User details fetch failed:", response.status, errorText);
        throw new Error("Unable to load user details. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data };
}

/**
 * User: update membership/credits/avatar.
 * RQ: { avatar_url?: string, firstName?: string, lastName?: string, ... }
 */
export async function updateUser(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/user`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        throw new Error("Unable to update user profile");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Upload User Avatar
 * POST /api/user/avatar
 * RQ: FormData { file: File }
 * RS: { avatar_url: string }
 */
export async function uploadUserAvatar(file) {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetchWithAuth(`${BASE_URL}/api/user/avatar`, {
        method: "POST",
        body: formData
    });

    if (!response.ok) {
        throw new Error("Unable to upload avatar");
    }

    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Accept collaboration invite (public)
 * RQ: POST /api/collaboration/invites/{token}/accept
 * RS: { status: 200, data: { caseId: string, status: string } }
 */
export async function acceptCollaborationInvite(token) {
    if (!token) {
        throw new Error("Missing invite token.");
    }
    const response = await fetch(`${BASE_URL}/api/collaboration/invites/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        headers: { accept: "application/json" }
    });
    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Invite acceptance failed:", response.status, errorText);
        throw new Error("Unable to accept invite. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}
